import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Search, Trash2, Pencil, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

function CustForm({ initial, onSubmit, submitLabel = "Save" }) {
  const [form, setForm] = useState(
    initial || {
      customer_name: "", company_name: "", gst: "", mobile: "", email: "",
      address: "", state: "", city: "", pincode: "", active: true,
    }
  );
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => { e.preventDefault(); onSubmit(form); }}
      data-testid="vcust-form"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Customer Name *</Label>
          <Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="vcust-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Company / Shop</Label>
          <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Mobile *</Label>
          <Input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} data-testid="vcust-mobile-input" />
        </div>
        <div className="space-y-2">
          <Label>GST Number</Label>
          <Input value={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.value.toUpperCase() })} />
        </div>
        <div className="space-y-2">
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>State</Label>
          <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>City</Label>
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Pincode</Label>
          <Input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Address</Label>
        <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
      </div>
      <DialogFooter>
        <Button type="submit" className="bg-zinc-900 hover:bg-zinc-800" data-testid="vcust-form-submit">
          {submitLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

export default function VoucherCustomers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);

  const load = async (search = "") => {
    const { data } = await api.get("/voucher/customers", { params: search ? { q: search } : {} });
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (form) => {
    try {
      const { data } = await api.post("/voucher/customers", form);
      toast.success("Customer added");
      setOpen(false);
      setItems([data, ...items]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add");
    }
  };
  const onUpdate = async (form) => {
    try {
      const { data } = await api.put(`/voucher/customers/${editTarget.id}`, form);
      toast.success("Customer updated");
      setEditTarget(null);
      setItems(items.map((c) => (c.id === data.id ? data : c)));
    } catch {
      toast.error("Could not update");
    }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    try {
      await api.delete(`/voucher/customers/${id}`);
      setItems(items.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline text-zinc-500">Voucher ERP</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Customer Master</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-zinc-900 hover:bg-zinc-800" data-testid="add-vcust-button">
              <Plus className="w-4 h-4 mr-1.5" /> Add Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>New Voucher Customer</DialogTitle>
            </DialogHeader>
            <CustForm onSubmit={onCreate} submitLabel="Add Customer" />
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
        <Input
          placeholder="Search by name, company, mobile, GST…"
          value={q}
          onChange={(e) => { setQ(e.target.value); load(e.target.value); }}
          className="pl-9"
          data-testid="vcust-search-input"
        />
      </div>

      <Card className="p-0 overflow-hidden">
        {items.length === 0 ? (
          <div className="p-12 text-center text-zinc-500 text-sm">No customers yet.</div>
        ) : (
          <div className="divide-y divide-border" data-testid="vcust-list">
            {/* Mobile card view */}
            <div className="md:hidden divide-y divide-border">
              {items.map((c) => (
                <div key={c.id} className="p-4 hover:bg-zinc-50" data-testid={`vcust-row-${c.id}`}>
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="font-medium truncate">{c.customer_name}</div>
                  </div>
                  <div className="text-xs text-zinc-500 truncate mb-2">{c.company_name || "—"} · {c.city || "—"}</div>
                  <div className="grid grid-cols-3 gap-2 text-sm mb-2">
                    <div><span className="text-zinc-500">Mobile:</span> <span className="num">{c.mobile}</span></div>
                    <div><span className="text-zinc-500">GST:</span> <span className="font-mono truncate">{c.gst || "—"}</span></div>
                  </div>
                  {c.mobile && (
                    <div className="flex items-center gap-2 justify-end">
                      <a href={`tel:${c.mobile}`}><Button size="icon" variant="outline" className="h-8 w-8"><Phone className="w-3.5 h-3.5" /></Button></a>
                      <a href={`https://wa.me/${c.mobile.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer">
                        <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-600"><MessageCircle className="w-3.5 h-3.5" /></Button>
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-1 justify-end mt-2">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditTarget(c)} data-testid={`edit-vcust-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {isAdmin && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => onDelete(c.id)} data-testid={`delete-vcust-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {/* Desktop table view */}
            <div className="hidden md:block">
              {items.map((c) => (
                <div key={c.id} className="grid grid-cols-[1fr_160px_160px_auto] items-center gap-3 p-4 hover:bg-zinc-50" data-testid={`vcust-row-${c.id}`}>
                  <div className="min-w-0">
                    <div className="font-medium">{c.customer_name}</div>
                    <div className="text-xs text-zinc-500 truncate">{c.company_name || "—"} · {c.city || "—"}</div>
                  </div>
                  <div className="text-sm num">{c.mobile}</div>
                  <div className="text-xs text-zinc-500 font-mono truncate">{c.gst || "—"}</div>
                  <div className="flex items-center gap-1 justify-end">
                    {c.mobile && (
                      <>
                        <a href={`tel:${c.mobile}`}><Button size="icon" variant="outline" className="h-8 w-8"><Phone className="w-3.5 h-3.5" /></Button></a>
                        <a href={`https://wa.me/${c.mobile.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer">
                          <Button size="icon" variant="outline" className="h-8 w-8 text-emerald-600"><MessageCircle className="w-3.5 h-3.5" /></Button>
                        </a>
                      </>
                    )}
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditTarget(c)} data-testid={`edit-vcust-${c.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    {isAdmin && (
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-red-600 hover:text-red-700" onClick={() => onDelete(c.id)} data-testid={`delete-vcust-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
          </DialogHeader>
          {editTarget && <CustForm initial={editTarget} onSubmit={onUpdate} submitLabel="Save Changes" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
